/*
 * Viewport-gated video playback.
 *
 * A video only downloads once it is close to the viewport, and only plays while
 * it is actually on screen. Calling play() on a video forces the browser to
 * buffer the whole file regardless of the preload attribute, so playback must
 * stay behind the observer or every clip on the page downloads in full.
 */
(function () {
  'use strict';

  var PLAY_RATIO = 0.25;
  var NEAR_VIEWPORT = '200px 0px';

  function prefersReducedMotion() {
    return window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  function savesData() {
    var connection = navigator.connection || navigator.webkitConnection;
    if (!connection) return false;
    if (connection.saveData) return true;
    return /(^|-)2g$/.test(connection.effectiveType || '');
  }

  var holdBack = prefersReducedMotion() || savesData();

  /* Videos stay parked until the loader observer says they are near the viewport. */
  function park(video) {
    if (video.dataset.pkVideoParked === 'true') return;
    video.dataset.pkVideoParked = 'true';

    video.muted = true;
    video.defaultMuted = true;
    video.setAttribute('muted', '');
    video.loop = true;
    video.setAttribute('loop', '');
    video.playsInline = true;
    video.setAttribute('playsinline', '');
    video.setAttribute('webkit-playsinline', '');

    // Autoplay attribute would defeat the gate: the browser starts fetching as
    // soon as a source is attached, before the observer ever fires.
    video.autoplay = false;
    video.removeAttribute('autoplay');
    video.preload = 'none';
    video.setAttribute('preload', 'none');
  }

  function wake(video) {
    if (video.dataset.pkVideoWoken === 'true') return;
    video.dataset.pkVideoWoken = 'true';
    video.preload = 'metadata';
    video.setAttribute('preload', 'metadata');
    video.load();
  }

  function play(video) {
    if (holdBack) return;
    var attempt = video.play();
    if (attempt && typeof attempt.catch === 'function') attempt.catch(function () {});
  }

  var loader = 'IntersectionObserver' in window ? new IntersectionObserver(function (entries) {
    entries.forEach(function (entry) {
      if (!entry.isIntersecting) return;
      wake(entry.target);
      loader.unobserve(entry.target);
    });
  }, { rootMargin: NEAR_VIEWPORT }) : null;

  var player = 'IntersectionObserver' in window ? new IntersectionObserver(function (entries) {
    entries.forEach(function (entry) {
      var video = entry.target;
      if (entry.isIntersecting && entry.intersectionRatio >= PLAY_RATIO) {
        wake(video);
        play(video);
      } else if (!video.paused) {
        video.pause();
      }
    });
  }, { threshold: [0, PLAY_RATIO] }) : null;

  function register(video) {
    /* Floating product videos own their scroll-triggered playback lifecycle.
       Registering them here created two competing controllers: this observer
       could park/pause the hidden player exactly as its section revealed and
       played it, so the first scroll only initialized the media and the second
       scroll finally played it. Keep sticky videos completely out of the global
       viewport controller; all other page videos continue using this gate. */
    if (video.matches && video.matches('[data-pksv-video]')) return;
    if (video.dataset.pkVideoReady === 'true') return;
    video.dataset.pkVideoReady = 'true';

    // A video the merchant explicitly gave controls to and that the visitor has
    // started themselves should never be paused out from under them.
    video.addEventListener('play', function () {
      if (video.dataset.pkVideoUser === 'true') player.unobserve(video);
    });
    video.addEventListener('click', function () { video.dataset.pkVideoUser = 'true'; });

    park(video);

    if (!loader || !player) {
      // No IntersectionObserver: leave the poster up rather than pull the file.
      return;
    }

    loader.observe(video);
    player.observe(video);
  }

  function scan(root) {
    var scope = root && root.querySelectorAll ? root : document;
    if (scope.matches && scope.matches('video')) register(scope);
    scope.querySelectorAll('video').forEach(register);
  }

  function start() {
    scan(document);
    // Section re-render in the theme editor is the only case that injects new
    // videos, so there is no need to observe the whole document for mutations.
    document.addEventListener('shopify:section:load', function (event) { scan(event.target); });
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start);
  else start();
})();
